import { useEffect, useMemo, useRef, useState } from "react";
import { Box, RefreshCw, Upload, RotateCcw, MousePointer2, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { api } from "../../lib/api";

const GEOMETRY_PRIORITY = { glb: 0, gltf: 1, dae: 2, obj: 3 };

function geometryPriority(file) {
  const name = String(file?.filename || file?.original_filename || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return GEOMETRY_PRIORITY[ext] ?? 99;
}

function preferredGeometryId(group, fileMap) {
  const ids = [...(group?.geometry_export_file_ids || [])];
  ids.sort((a, b) => {
    const fa = fileMap?.[a] || {};
    const fb = fileMap?.[b] || {};
    const byFormat = geometryPriority(fa) - geometryPriority(fb);
    if (byFormat !== 0) return byFormat;
    return Number(fa?.size || Number.MAX_SAFE_INTEGER) - Number(fb?.size || Number.MAX_SAFE_INTEGER);
  });
  return ids[0] || "";
}

function disposeObject(root) {
  root?.traverse?.((obj) => {
    if (obj.geometry) obj.geometry.dispose?.();
    if (obj.material) {
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      materials.forEach((m) => {
        Object.values(m || {}).forEach((v) => v?.isTexture && v.dispose?.());
        m?.dispose?.();
      });
    }
  });
}

function Viewer3D({ fileId, filename, onSelection, highlightIds = [] }) {
  const viewerRef = useRef(null);
  const hostRef = useRef(null);
  const resetRef = useRef(() => {});
  const rotateRef = useRef(() => {});
  const highlightRef = useRef(() => {});
  const pendingHighlightIdsRef = useRef(highlightIds);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreen = () => setFullscreen(document.fullscreenElement === viewerRef.current);
    document.addEventListener("fullscreenchange", handleFullscreen);
    return () => document.removeEventListener("fullscreenchange", handleFullscreen);
  }, []);

  const toggleFullscreen = async () => {
    const el = viewerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) await document.exitFullscreen();
      else if (el.requestFullscreen) await el.requestFullscreen();
      else throw new Error("Schermo intero non supportato dal browser");
    } catch (e) {
      toast.error(e?.message || "Impossibile aprire lo schermo intero");
    }
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !fileId) return;

    let disposed = false;
    let model = null;
    let raf = 0;
    let resizeObserver = null;
    let blobUrl = null;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100000);
    camera.position.set(10, 10, 10);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = false;
    renderer.domElement.className = "w-full h-full block";
    host.innerHTML = "";
    host.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    scene.add(new THREE.HemisphereLight(0xffffff, 0x64748b, 1.8));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(20, 30, 15);
    scene.add(key);

    const grid = new THREE.GridHelper(100, 40, 0x94a3b8, 0xcbd5e1);
    grid.material.opacity = 0.45;
    grid.material.transparent = true;
    scene.add(grid);
    scene.add(new THREE.AxesHelper(2));

    const fitBox = (box) => {
      if (!box || box.isEmpty()) return;
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z, 1);
      const dist = maxDim * 1.8;
      camera.near = Math.max(maxDim / 10000, 0.01);
      camera.far = maxDim * 100;
      camera.updateProjectionMatrix();
      camera.position.set(center.x + dist, center.y + dist * 0.7, center.z + dist);
      controls.target.copy(center);
      controls.update();
    };

    const fit = () => {
      if (!model) return;
      const box = new THREE.Box3().setFromObject(model);
      fitBox(box);
      if (!box.isEmpty()) {
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z, 1);
        grid.position.y = box.min.y;
        grid.scale.setScalar(Math.max(1, maxDim / 100));
      }
    };
    resetRef.current = fit;

    let baseRotationX = 0;
    let manualRotationX = 0;
    rotateRef.current = (delta = null) => {
      if (!model) return;
      if (delta === null) manualRotationX = 0;
      else manualRotationX += delta;
      model.rotation.x = baseRotationX + manualRotationX;
      model.updateMatrixWorld(true);
      fit();
    };

    const elementIdForObject = (obj) => {
      let current = obj;
      while (current && current !== model?.parent) {
        if (/^\d+$/.test(String(current.name || ""))) return String(current.name);
        current = current.parent;
      }
      return null;
    };

    let highlightedMeshes = [];
    const clearHighlight = () => {
      for (const mesh of highlightedMeshes) {
        const original = mesh.userData.__bimOriginalMaterial;
        if (original) {
          const current = mesh.material;
          mesh.material = original;
          const mats = Array.isArray(current) ? current : [current];
          mats.forEach((m) => {
            if (m && m !== original && !Array.isArray(original)) m.dispose?.();
          });
          delete mesh.userData.__bimOriginalMaterial;
        }
      }
      highlightedMeshes = [];
    };

    const applyHighlight = (ids = []) => {
      clearHighlight();
      if (!model || !ids?.length) return;
      const wanted = new Set(ids.map((x) => String(x)));
      const box = new THREE.Box3();
      let matched = 0;
      model.traverse((obj) => {
        if (!obj.isMesh) return;
        const elementId = elementIdForObject(obj);
        if (!elementId || !wanted.has(elementId)) return;
        obj.userData.__bimOriginalMaterial = obj.material;
        const sourceMaterials = Array.isArray(obj.material) ? obj.material : [obj.material];
        const highlighted = sourceMaterials.map((source) => {
          const m = source.clone();
          if (m.color) m.color.set(0xffb000);
          if (m.emissive) {
            m.emissive.set(0xff8a00);
            m.emissiveIntensity = 0.55;
          }
          m.transparent = false;
          return m;
        });
        obj.material = Array.isArray(obj.material) ? highlighted : highlighted[0];
        highlightedMeshes.push(obj);
        box.expandByObject(obj);
        matched += 1;
      });
      if (matched > 0 && !box.isEmpty()) fitBox(box);
    };
    highlightRef.current = applyHighlight;

    const resize = () => {
      const w = Math.max(host.clientWidth, 1);
      const h = Math.max(host.clientHeight, 1);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const handleClick = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(scene.children, true).filter((h) => h.object?.isMesh);
      const obj = hits[0]?.object || null;
      onSelection?.(obj ? {
        name: obj.name || obj.parent?.name || "Elemento 3D",
        type: obj.type,
        uuid: obj.uuid,
        elementId: elementIdForObject(obj),
      } : null);
    };
    renderer.domElement.addEventListener("click", handleClick);

    const animate = () => {
      if (disposed) return;
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const loadModel = async () => {
      setLoading(true);
      setError("");
      try {
        const blob = await api.fileBlob(fileId);
        if (disposed) return;
        const ext = (filename || "").toLowerCase().split(".").pop();
        if (ext === "dae") {
          let text = await blob.text();
          text = text.replace(/<node id="(\d+)" name="node"/g, '<node id="$1" name="$1"');
          const result = new ColladaLoader().parse(text, "");
          model = result.scene;
        } else if (ext === "obj") {
          const text = await blob.text();
          model = new OBJLoader().parse(text);
        } else if (ext === "glb" || ext === "gltf") {
          const data = ext === "glb" ? await blob.arrayBuffer() : await blob.text();
          model = await new Promise((resolve, reject) => {
            new GLTFLoader().parse(data, "", (gltf) => resolve(gltf.scene), reject);
          });
          baseRotationX = -Math.PI / 2;
          manualRotationX = 0;
          model.rotation.x = baseRotationX;
          model.updateMatrixWorld(true);
        } else {
          throw new Error("Formato geometrico non supportato dal viewer");
        }
        if (disposed) return;
        model.name = model.name || filename || "BIM model";
        scene.add(model);
        fit();
        applyHighlight(pendingHighlightIdsRef.current);
      } catch (e) {
        setError(e?.message || "Impossibile caricare la geometria BIM");
      } finally {
        if (!disposed) setLoading(false);
      }
    };
    loadModel();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      renderer.domElement.removeEventListener("click", handleClick);
      controls.dispose();
      clearHighlight();
      if (model) {
        scene.remove(model);
        disposeObject(model);
      }
      renderer.dispose();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      host.innerHTML = "";
    };
  }, [fileId, filename, onSelection]);

  useEffect(() => {
    pendingHighlightIdsRef.current = highlightIds;
    highlightRef.current?.(highlightIds);
  }, [highlightIds]);

  return (
    <div ref={viewerRef} className={"relative rounded-xl border border-slate-200 overflow-hidden bg-slate-50 " + (fullscreen ? "w-screen h-screen rounded-none border-0" : "")}>
      <div ref={hostRef} className={fullscreen ? "w-full h-full" : "h-[560px] min-h-[360px]"} />
      <div className="absolute top-3 left-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => resetRef.current?.()} className="inline-flex items-center gap-1.5 rounded-lg bg-white/95 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
          <RotateCcw className="w-3.5 h-3.5" /> Adatta vista
        </button>
        <button type="button" onClick={toggleFullscreen} className="inline-flex items-center gap-1.5 rounded-lg bg-white/95 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
          {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          {fullscreen ? "Esci schermo intero" : "Schermo intero"}
        </button>
        <button type="button" onClick={() => rotateRef.current?.(-Math.PI / 2)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/95 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" title="Ruota modello di -90°">
          ↺ -90°
        </button>
        <button type="button" onClick={() => rotateRef.current?.(Math.PI / 2)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/95 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" title="Ruota modello di +90°">
          ↻ +90°
        </button>
        <button type="button" onClick={() => rotateRef.current?.(null)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/95 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" title="Ripristina orientamento automatico">
          Reset orientamento
        </button>
      </div>
      {loading && <div className="absolute inset-0 grid place-items-center bg-white/75 text-sm text-slate-500">Caricamento geometria 3D…</div>}
      {error && <div className="absolute inset-0 grid place-items-center bg-white/90 p-8 text-center"><div><div className="font-semibold text-red-700">Viewer non disponibile</div><div className="text-xs text-slate-500 mt-1">{error}</div></div></div>}
    </div>
  );
}

export default function BimViewerTab({ offerId }) {
  const [manifest, setManifest] = useState(null);
  const [modelLinks, setModelLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [selectedGeometryId, setSelectedGeometryId] = useState("");
  const [loadedGeometryId, setLoadedGeometryId] = useState("");
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedElementDetail, setSelectedElementDetail] = useState(null);
  const [elementDetailLoading, setElementDetailLoading] = useState(false);
  const [highlightIds, setHighlightIds] = useState([]);
  const [selectedProjectCode, setSelectedProjectCode] = useState("");
  const [linkLoading, setLinkLoading] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async (rebuild = false) => {
    setLoading(true);
    try {
      const data = rebuild
        ? await api.takeoffBuildProjectManifest(offerId)
        : await api.takeoffProjectManifest(offerId);
      setManifest(data);
      const groups = data?.bim_model_groups || [];
      const fileMap = Object.fromEntries((data?.files || []).map((f) => [f.file_id, f]));
      const current = groups.find((g) => g.bim_model_key === selectedGroupKey);
      const candidates = groups
        .map((g) => {
          const id = preferredGeometryId(g, fileMap);
          return { group: g, id, size: Number(fileMap[id]?.size || Number.MAX_SAFE_INTEGER) };
        })
        .filter((x) => x.id);
      candidates.sort((a, b) => a.size - b.size);
      const nextGroup = current || candidates[0]?.group || groups[0];
      if (nextGroup) {
        setSelectedGroupKey(nextGroup.bim_model_key);
        const geometryIds = nextGroup.geometry_export_file_ids || [];
        if (!geometryIds.includes(selectedGeometryId)) {
          setSelectedGeometryId(preferredGeometryId(nextGroup, fileMap));
          setLoadedGeometryId("");
        }
      } else {
        setSelectedGroupKey("");
        setSelectedGeometryId("");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manifest BIM non disponibile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [offerId]);

  const groups = manifest?.bim_model_groups || [];
  const filesById = useMemo(() => {
    const out = {};
    for (const f of manifest?.files || []) out[f.file_id] = f;
    return out;
  }, [manifest]);
  const selectedGroup = groups.find((g) => g.bim_model_key === selectedGroupKey) || groups[0] || null;
  const geometryIds = selectedGroup?.geometry_export_file_ids || [];
  const selectedGeometry = filesById[selectedGeometryId] || null;
  const selectedQuantityFileId = (selectedGroup?.quantity_export_file_ids || [])[0] || "";

  useEffect(() => {
    setLoadedGeometryId(selectedGeometryId || "");
    setSelectedObject(null);
  }, [selectedGeometryId]);

  const linkedRows = modelLinks?.file_id === selectedQuantityFileId ? (modelLinks?.links || []) : [];

  useEffect(() => {
    if (!selectedQuantityFileId || !selectedObject?.elementId) {
      setSelectedElementDetail(null);
      setElementDetailLoading(false);
      return;
    }
    let cancelled = false;
    setElementDetailLoading(true);
    api.takeoffBimElementDetail(selectedQuantityFileId, selectedObject.elementId)
      .then((data) => { if (!cancelled) setSelectedElementDetail(data); })
      .catch(() => { if (!cancelled) setSelectedElementDetail(null); })
      .finally(() => { if (!cancelled) setElementDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedQuantityFileId, selectedObject?.elementId]);

  useEffect(() => {
    if (!selectedQuantityFileId) {
      setModelLinks(null);
      setLinksLoading(false);
      return;
    }
    let cancelled = false;
    setModelLinks(null);
    setLinksLoading(true);
    api.takeoffBimCmeLinks(offerId, selectedQuantityFileId)
      .then((data) => { if (!cancelled) setModelLinks(data); })
      .catch(() => { if (!cancelled) setModelLinks({ file_id: selectedQuantityFileId, links: [] }); })
      .finally(() => { if (!cancelled) setLinksLoading(false); });
    return () => { cancelled = true; };
  }, [offerId, selectedQuantityFileId]);

  const chooseGroup = (group) => {
    setSelectedGroupKey(group.bim_model_key);
    setSelectedGeometryId(preferredGeometryId(group, filesById));
    setLoadedGeometryId("");
    setModelLinks(null);
    setLinksLoading(true);
    setSelectedObject(null);
    setHighlightIds([]);
    setSelectedProjectCode("");
  };

  const focusCmeRow = async (row) => {
    if (!row?.project_code) return;
    setLinkLoading(row.project_code);
    try {
      const sourceFileId = (selectedGroup?.quantity_export_file_ids || [])[0] || null;
      const result = await api.takeoffBimCmeElements(offerId, row.project_code, sourceFileId);
      const sourceIds = new Set(selectedGroup?.quantity_export_file_ids || []);
      const ids = (result.elements || [])
        .filter((el) => !sourceIds.size || sourceIds.has(el.file_id))
        .map((el) => String(el.element_id))
        .filter(Boolean);
      setSelectedProjectCode(row.project_code);
      setHighlightIds(ids);
      setSelectedObject(null);
      if (selectedGeometryId) setLoadedGeometryId(selectedGeometryId);
      if (!ids.length) toast.warning("Nessun elemento BIM trovato per questa voce nel modello selezionato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Collegamento computo-BIM non disponibile");
    } finally {
      setLinkLoading("");
    }
  };

  const uploadGeometry = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadFile(offerId, file);
      await load(true);
      toast.success("Geometria BIM caricata e indicizzata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento geometria non riuscito");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-sm text-slate-400">Caricamento modelli BIM…</div>;

  return (
    <div className="space-y-4 fade-up">
      <input ref={fileInputRef} type="file" accept=".dae,.glb,.gltf,.obj" className="hidden" onChange={uploadGeometry} />

      {groups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Box className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="font-semibold text-slate-700 mt-3">Nessun modello BIM disponibile</div>
        </div>
      ) : (
        <div className="grid xl:grid-cols-[280px_minmax(0,1fr)] gap-4">
          <div className="space-y-3">
            {groups.map((group) => {
              const active = group.bim_model_key === selectedGroup?.bim_model_key;
              return (
                <button key={group.bim_model_key} type="button" onClick={() => chooseGroup(group)} className={"w-full text-left bg-white border rounded-xl p-4 transition " + (active ? "border-sky-500 ring-2 ring-sky-100" : "border-slate-200 hover:border-slate-300")}>
                  <div className="font-semibold text-slate-900 break-words">{group.bim_model_key}</div>
                  <div className="text-[11px] text-slate-500 mt-1">Disciplina {group.discipline || "—"}</div>
                  <div className="flex flex-wrap gap-1.5 mt-3 text-[10px]">
                    <span className={group.has_source_rvt ? "rounded bg-emerald-50 text-emerald-700 px-2 py-1" : "rounded bg-slate-50 text-slate-400 px-2 py-1"}>RVT {group.has_source_rvt ? "✓" : "—"}</span>
                    <span className={group.has_ifc ? "rounded bg-emerald-50 text-emerald-700 px-2 py-1" : "rounded bg-slate-50 text-slate-400 px-2 py-1"}>IFC {group.has_ifc ? "✓" : "—"}</span>
                    <span className={group.has_structured_quantities ? "rounded bg-emerald-50 text-emerald-700 px-2 py-1" : "rounded bg-slate-50 text-slate-400 px-2 py-1"}>Dati {group.has_structured_quantities ? "✓" : "—"}</span>
                    <span className={(group.geometry_export_file_ids || []).length ? "rounded bg-violet-50 text-violet-700 px-2 py-1" : "rounded bg-amber-50 text-amber-700 px-2 py-1"}>3D {(group.geometry_export_file_ids || []).length ? "✓" : "manca"}</span>
                  </div>
                  {group.analysis_ready && <div className="mt-3 text-xs text-slate-600">{group.bim_element_count || 0} elementi indicizzati</div>}
                </button>
              );
            })}

            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-sky-600 font-semibold">Computo collegato</div>
              <div className="font-semibold text-slate-800 mt-0.5">Voci BIM del modello</div>
              <div className="text-[10px] text-slate-400 mt-1">{linksLoading ? "Caricamento voci…" : linkedRows.length + " voci con corrispondenza BIM"}</div>
              <div className="mt-3 max-h-[520px] overflow-y-auto space-y-1.5 pr-1">
                {linksLoading ? <div className="py-4 text-center text-xs text-slate-400">Caricamento voci del modello selezionato…</div> : linkedRows.length ? linkedRows.map((row) => {
                  const active = selectedProjectCode === row.project_code;
                  return (
                    <button key={row.project_code} type="button" onClick={() => focusCmeRow(row)} disabled={linkLoading === row.project_code}
                      className={"w-full text-left rounded-lg border px-3 py-2 transition " + (active ? "border-orange-400 bg-orange-50" : "border-slate-100 bg-slate-50 hover:border-sky-300 hover:bg-sky-50")}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs font-bold text-slate-800">{row.code || row.project_code}</span>
                        <span className="text-[10px] text-slate-400">{row.element_count == null ? "collegato" : `${row.element_count} elem.`}</span>
                      </div>
                      <div className="mt-1 text-[11px] leading-snug text-slate-600 line-clamp-3">{row.description || "—"}</div>
                      <div className="mt-1 text-[10px] text-slate-400">{row.quantity ?? "—"} {row.unit || ""}{row.unit_price != null ? " · € " + Number(row.unit_price).toLocaleString("it-IT", { maximumFractionDigits: 2 }) : ""}</div>
                    </button>
                  );
                }) : <div className="py-4 text-center text-xs text-slate-400">Nessuna voce di computo collegata a questo modello.</div>}
              </div>
            </div>
          </div>

          <div className="space-y-3 min-w-0">
            {selectedGeometryId ? (
              <>
                {loadedGeometryId === selectedGeometryId ? (
                  <Viewer3D fileId={loadedGeometryId} filename={selectedGeometry?.filename || ""} onSelection={setSelectedObject} highlightIds={highlightIds} />
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-16 text-center text-sm text-slate-500">
                    Premi <b>Apri modello 3D</b> per scaricare e renderizzare la geometria.
                  </div>
                )}
                <div className="bg-white border border-slate-200 rounded-xl p-4 text-xs">
                  <div className="flex items-center gap-2 font-semibold text-slate-700"><MousePointer2 className="w-4 h-4 text-sky-600" /> Scheda elemento BIM</div>
                  {!selectedObject ? (
                    <div className="mt-2 text-slate-400">Clicca un oggetto nel modello per leggere i dati Revit associati.</div>
                  ) : elementDetailLoading ? (
                    <div className="mt-3 text-slate-400">Caricamento dati elemento…</div>
                  ) : selectedElementDetail?.element ? (() => {
                    const el = selectedElementDetail.element;
                    const pp = selectedElementDetail.preferred_parameters || {};
                    const prettyCategory = String(el.category || "—").replace(/^OST_/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
                    const fmt = (v, unit = "") => v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 3 }) + (unit ? " " + unit : "");
                    return (
                      <div className="mt-3 space-y-4">
                        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                          <div><div className="text-slate-400">Categoria</div><div className="font-semibold text-slate-800">{prettyCategory}</div></div>
                          <div><div className="text-slate-400">Famiglia</div><div className="font-semibold text-slate-800 break-words">{el.family || pp["Family Name : String"] || "—"}</div></div>
                          <div><div className="text-slate-400">Tipo</div><div className="font-semibold text-slate-800 break-words">{el.type || pp["[Type] Type Name : String"] || "—"}</div></div>
                          <div><div className="text-slate-400">Livello</div><div className="font-semibold text-slate-800">{el.level || pp["Base Level : String"] || "—"}</div></div>
                        </div>

                        {(pp["[Type] Description : String"] || pp["[Type] Classification Title : String"]) && (
                          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                            <div className="text-slate-400">Descrizione tecnica</div>
                            <div className="mt-1 text-sm font-medium text-slate-800">{pp["[Type] Description : String"] || pp["[Type] Classification Title : String"]}</div>
                            {pp["[Type] Classification Number : String"] && <div className="mt-1 font-mono text-[11px] text-slate-500">{pp["[Type] Classification Number : String"]}</div>}
                          </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div><div className="text-slate-400">Volume</div><div className="font-semibold">{fmt(el.volume, "m³")}</div></div>
                          <div><div className="text-slate-400">Area</div><div className="font-semibold">{fmt(el.area, "m²")}</div></div>
                          <div><div className="text-slate-400">Lunghezza</div><div className="font-semibold">{fmt(el.length ?? pp["System Length : Double"], "m")}</div></div>
                          <div><div className="text-slate-400">Element ID</div><div className="font-mono font-semibold">{el.element_id || selectedObject.elementId}</div></div>
                        </div>

                        {(selectedElementDetail.materials || []).length > 0 && (
                          <div>
                            <div className="font-semibold text-slate-700">Materiali</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {selectedElementDetail.materials.map((m, i) => (
                                <div key={m.name + i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                  <div className="font-medium text-slate-800">{m.name}</div>
                                  <div className="text-[10px] text-slate-500 mt-0.5">{m.volume != null ? fmt(m.volume, "m³") : ""}{m.area != null ? (m.volume != null ? " · " : "") + fmt(m.area, "m²") : ""}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 text-[11px]">
                          {pp["Phase Created : String"] && <div><span className="text-slate-400">Fase creazione</span><div>{pp["Phase Created : String"]}</div></div>}
                          {pp["Workset : String"] && <div><span className="text-slate-400">Workset</span><div>{pp["Workset : String"]}</div></div>}
                          {el.unique_id && <div><span className="text-slate-400">UniqueId</span><div className="font-mono break-all">{el.unique_id}</div></div>}
                          {pp["IfcGUID : String"] && <div><span className="text-slate-400">IFC GUID</span><div className="font-mono break-all">{pp["IfcGUID : String"]}</div></div>}
                        </div>

                        {(selectedElementDetail.linked_cme || []).length > 0 && (
                          <div className="border-t border-slate-100 pt-3">
                            <div className="font-semibold text-slate-700">Voci di computo collegate</div>
                            <div className="mt-2 grid md:grid-cols-2 gap-2">
                              {selectedElementDetail.linked_cme.map((row) => (
                                <div key={row.id || row.code} className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
                                  <div className="flex justify-between gap-2"><span className="font-mono font-bold text-orange-800">{row.code}</span><span className="text-[10px] text-orange-600">{row.quantity ?? "—"} {row.unit || ""}</span></div>
                                  <div className="mt-1 text-[11px] text-slate-700 line-clamp-2">{row.description}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="mt-2 text-slate-400">Element ID {selectedObject.elementId || "—"} selezionato, ma nessun dettaglio strutturato è stato trovato.</div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white border border-amber-200 rounded-xl p-8 text-center">
                <Box className="w-9 h-9 text-amber-400 mx-auto" />
                <div className="font-semibold text-slate-800 mt-3">Dati BIM disponibili, geometria 3D non caricata</div>
                <div className="text-xs text-slate-500 mt-2 max-w-xl mx-auto">
                  L'export CADBIMConverter indica una geometria DAE associata al modello. Carica il relativo file <b>.dae</b> con lo stesso nome base del modello; il sistema lo collegherà automaticamente.
                </div>
                <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-sky-700 px-3 py-2 text-sm font-semibold text-white"><Upload className="w-4 h-4" /> Carica file 3D</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
